import { styles } from '#pplx_generated/pplx/frontend/aether/primitives/ShimmerPrimitive/ShimmerPrimitive.module.css.js';
import classNames from 'classnames';
import React from 'react';
/**
 * Low-level primitive that applies an Aether shimmer effect to its children for skeleton-like
 * placeholders and lightweight highlight treatments.
 *
 * @category primitives
 * @keywords shimmer, primitive, primitives, shimmerprimitive, skeleton, placeholder, loading,
 *   highlight, effect, animation, children, classname, enabled, variant, default, super,
 *   super-text, iterations, spinner, progress, motion
 * @whenToUse Use ShimmerPrimitive when building a custom placeholder, skeleton-like surface, or
 *   lightweight highlight treatment that needs Aether's shimmer styling applied to existing
 *   children. It is a low-level primitive, not a concrete product loading component; compose the
 *   placeholder shape and loading semantics around it. Shimmer is delight layered on top of a
 *   clear loading state, not the primary way to gather attention or communicate that work is
 *   happening. For standard loading placeholders prefer Skeleton, and for activity indicators
 *   without placeholder content use Spinner or Progress instead.
 * @guidelineDo
 *   - Use ShimmerPrimitive for custom placeholder or skeleton-like surfaces that need the Aether
 *     shimmer treatment applied to provided children
 *   - Reserve shimmer for waits users actually watch: a noticeable, indeterminate wait of roughly
 *     a second or more where the user is likely looking at the region
 *   - Prefer Skeleton for standard loading placeholders; it carries the design system's optimized
 *     shimmer treatment
 *   - Use className to size and position the wrapper because the shimmer effect is applied
 *     through the wrapper around the children
 *   - Use enabled to keep placeholder children mounted while turning the shimmer effect on or off
 *   - Use iterations when a finite shimmer pass is needed instead of the CSS-defined animation
 *     behavior
 *   - Use as to pick the element tag for the shimmer container, which defaults to div, and pass
 *     an inline tag such as span when the shimmer sits inside a paragraph or other inline context
 *   - Use speed to choose the sweep duration, where normal runs 1200ms and slow runs 1800ms, and
 *     note that variant='super' defaults to slow
 *   - Use paused to freeze the animation at its current frame while keeping the shimmer mounted
 * @guidelineDont
 *   - Don't treat ShimmerPrimitive as a complete loading component; compose the placeholder
 *     shape, layout, and semantics around it
 *   - Don't use shimmer animation as the only indication that important content is loading;
 *     animation-only signals exclude screen reader users and are easy to miss in bright glare or
 *     at very low display brightness
 *   - Don't add shimmer to states that typically resolve in a split second; a brief flash of
 *     animation costs more than it communicates
 *   - Don't run several separate shimmering regions on the page at once; multiple shimmering
 *     lines or shapes inside one loading region are fine
 *   - Don't leave shimmering states mounted for long stretches; a long-lived loading state is a
 *     signal to make the underlying work faster, not to add more animation
 *   - Don't leave the default div element in an inline context; pass as so the rendered markup
 *     stays valid
 * @accessibility
 *   - ShimmerPrimitive itself is decorative; put loading semantics such as aria-busy or live
 *     status text on the owning region when users need to know content is loading
 *   - Children remain rendered when enabled is false, so make sure any placeholder content still
 *     has appropriate text alternatives or is hidden from assistive technology when decorative
 *   - Do not communicate state through shimmer animation alone; pair the placeholder with stable
 *     labels, structure, or status when the loading state matters
 * @relatedComponents spinner, progress, motion
 */
export const ShimmerPrimitive = React.memo(function Shimmer({ children, className, enabled = true, variant = 'default', iterations, as = 'div', speed, paused, }) {
    const resolvedSpeed = speed ?? (variant === 'super' ? 'slow' : 'normal');
    return React.createElement(as, {
        style: {
            animationIterationCount: iterations,
            animationFillMode: iterations !== undefined ? 'forwards' : undefined,
            animationPlayState: paused ? 'paused' : undefined,
        },
        className: classNames(resolvedSpeed === 'slow' ? styles.slowDuration : styles.sharedDuration, {
            [styles.defaultShimmer]: enabled && variant === 'default',
            [styles.super]: enabled && variant === 'super',
            [styles.superText]: enabled && variant === 'super-text',
        }, className),
    }, children);
});
//# sourceMappingURL=index.js.map